import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Waypoints, ExternalLink, CheckCircle2, Clock, TriangleAlert,
  RefreshCw, Trash2, ShieldCheck, Receipt, ArrowLeftRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import EmptyState from "@/components/EmptyState";
import { useModuleTour, TourButton } from "@/components/useModuleTour";
import SyncEnginePulse from "@/components/SyncEnginePulse";

function ProviderMark({ provider }) {
  const bg = provider.brand_color || "#c8a96a";
  const letter = provider.name.charAt(0);
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-xl"
      style={{
        background: `linear-gradient(180deg, ${bg}22, ${bg}05)`,
        border: `1px solid ${bg}55`,
        color: "#f5efe0",
      }}
      aria-hidden
    >
      {letter}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    connected: { label: "Connected", color: "text-emerald-300", bg: "bg-emerald-900/30", border: "border-emerald-500/40", Icon: CheckCircle2 },
    awaiting_credentials: { label: "Awaiting credentials", color: "text-amber-200", bg: "bg-amber-900/25", border: "border-amber-500/40", Icon: Clock },
    disconnected: { label: "Disconnected", color: "text-white/60", bg: "bg-white/5", border: "border-white/15", Icon: TriangleAlert },
    error: { label: "Error", color: "text-rose-200", bg: "bg-rose-900/30", border: "border-rose-500/40", Icon: TriangleAlert },
    not_set_up: { label: "Not set up", color: "text-white/50", bg: "bg-white/5", border: "border-white/10", Icon: Waypoints },
  };
  const s = map[status] || map.not_set_up;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tabular ${s.color} ${s.bg} border ${s.border}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

function formatTs(ts) {
  if (!ts) return "-";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function formatMoney(n, currency = "USD") {
  try {
    return Number(n || 0).toLocaleString(undefined, { style: "currency", currency });
  } catch {
    return `${currency} ${Number(n || 0).toFixed(2)}`;
  }
}

function ConnectionDialog({ open, dialog, onClose, onSave }) {
  const [accountRef, setAccountRef] = useState("");
  const [label, setLabel] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [authMode, setAuthMode] = useState("api_key");
  const [sandbox, setSandbox] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dialog?.existing) {
      setAccountRef(dialog.existing.account_ref || "");
      setLabel(dialog.existing.label || "");
      setAuthMode(dialog.existing.auth_mode || "api_key");
      setSandbox(dialog.existing.sandbox !== false);
      setAccessToken("");
    } else {
      setAccountRef("");
      setLabel("");
      setAuthMode("api_key");
      setSandbox(true);
      setAccessToken("");
    }
  }, [dialog]);

  if (!dialog) return null;
  const provider = dialog.provider;

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({ account_ref: accountRef, label, access_token: accessToken, auth_mode: authMode, sandbox });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass-card border-white/10 max-w-lg" data-testid={`sync-dialog-${provider.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            <ProviderMark provider={provider} />
            Connect {provider.name}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            {provider.tagline}. Just hit Save to try it instantly with sample invoices, or paste your live API key from the provider to sync real data. Your keys are locked down and only the last 4 characters are ever shown back to you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">Authentication</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="sync-auth-api-key"
                onClick={() => setAuthMode("api_key")}
                className={`rounded-lg border px-3 py-2 text-xs transition ${authMode === "api_key" ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}
              >
                API Key
              </button>
              <button
                type="button"
                data-testid="sync-auth-oauth"
                onClick={() => setAuthMode("oauth")}
                className={`rounded-lg border px-3 py-2 text-xs transition ${authMode === "oauth" ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}
              >
                OAuth Refresh Token
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">{provider.account_hint} (optional in sandbox)</Label>
            <Input
              value={accountRef}
              onChange={(e) => setAccountRef(e.target.value)}
              placeholder={`e.g. ${provider.account_hint} or leave blank`}
              data-testid="sync-account-ref"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Main venue, Sydney Central..."
              data-testid="sync-label"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">
              {authMode === "oauth" ? "OAuth Refresh Token" : provider.credential_hint} {sandbox ? "(optional in sandbox)" : ""}
              {dialog.existing?.has_token && (
                <span className="ml-2 text-emerald-300/80">On file, last 4: {dialog.existing.token_last4}</span>
              )}
            </Label>
            <Textarea
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={sandbox ? "Leave blank to run sandbox mode with sample data" : (dialog.existing?.has_token ? "Leave blank to keep the existing token" : "Paste your token")}
              rows={2}
              className="font-mono text-xs"
              data-testid="sync-access-token"
            />
            <a
              href={provider.docs_url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-amber-200/70 hover:text-amber-100 inline-flex items-center gap-1"
            >
              How to generate a live token <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <label className="flex items-center gap-2 text-xs text-white/70">
            <input
              type="checkbox"
              checked={sandbox}
              onChange={(e) => setSandbox(e.target.checked)}
              data-testid="sync-sandbox"
            />
            Sandbox mode (recommended: connect instantly with realistic sample invoices, no credentials needed)
          </label>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="sync-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="sync-dialog-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            <span className="ml-1.5">Connect and sync now</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SyncEngine() {
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [costHistory, setCostHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, c, inv, m, h] = await Promise.all([
        api.get("/sync/providers"),
        api.get("/sync/connections"),
        api.get("/sync/invoices", { params: { limit: 25 } }),
        api.get("/sync/mappings", { params: { limit: 200 } }),
        api.get("/sync/cost-history", { params: { limit: 50 } }),
      ]);
      setProviders(p.data?.providers || []);
      setConnections(c.data || []);
      setInvoices(inv.data?.invoices || []);
      setMappings(m.data?.mappings || []);
      setCostHistory(h.data?.history || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load the Sync Engine.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connByProvider = useMemo(() => {
    const map = {};
    for (const c of connections) map[c.provider] = c;
    return map;
  }, [connections]);

  const handleConnect = (provider) => {
    setDialog({ provider, existing: connByProvider[provider.id] || null });
  };

  const submitConnect = async ({ account_ref, label, access_token, auth_mode, sandbox }) => {
    if (!dialog) return;
    const provider = dialog.provider;
    try {
      const payload = {
        provider: provider.id,
        account_ref: account_ref || null,
        label: label || null,
        auth_mode,
        sandbox: !!sandbox,
      };
      if (access_token) payload.access_token = access_token;
      await api.post("/sync/connections", payload);
      setDialog(null);
      // Immediately fire a Sync Now so the operator sees invoices land
      // in the same click. This is what makes the flow feel plug and
      // play: click Connect, done, data on screen.
      try {
        const res = await api.post(`/sync/procurement/${provider.id}`);
        const r = res.data || {};
        if (r.status === "ok") {
          toast.success(`${provider.name} connected. ${r.invoices_synced} invoices and ${r.ingredients_updated} cost updates just landed. Scroll down to see them.`, {
            description: r.ingredients_created ? `Also added ${r.ingredients_created} new ingredients to your pantry.` : undefined,
            duration: 6500,
          });
          // Auto scroll to the Invoices tab so the operator physically
          // sees where the data landed. No more "did anything happen?".
          setTimeout(() => {
            document.querySelector('[data-testid="sync-tab-invoices"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 400);
        } else {
          toast.message(`${provider.name} saved`, { description: r.message || "Ready to sync." });
        }
      } catch (e) {
        toast.success(`${provider.name} saved. Hit Sync now to pull invoices.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save connection.");
    }
  };

  const handleSync = async (provider) => {
    const conn = connByProvider[provider.id];
    if (!conn) return handleConnect(provider);
    setBusy(provider.id);
    try {
      const res = await api.post(`/sync/procurement/${provider.id}`);
      const { status, message, invoices_synced, ingredients_updated, ingredients_created } = res.data || {};
      if (status === "ok") {
        toast.success(`${provider.name} synced`, {
          description: `${invoices_synced} invoice(s), ${ingredients_updated} cost update(s), ${ingredients_created} new ingredient(s).`,
        });
      } else if (status === "pending_credentials") {
        toast.message(`${provider.name}: awaiting credentials`, { description: message });
      } else {
        toast.error(`${provider.name} sync: ${message || "unknown status"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async (conn, provider) => {
    if (!window.confirm(`Disconnect ${provider.name}? Historical invoices stay saved.`)) return;
    try {
      await api.delete(`/sync/connections/${conn.id}`);
      toast.success(`${provider.name} disconnected.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not disconnect.");
    }
  };

  const syncTourSteps = [
    {
      selector: '[data-testid="sync-provider-grid"]',
      title: "Every supplier lives here",
      body: "PurchasePlus, MarketMan, Fourth: pick the one you use and hit Connect. Ready in a single tap on the sandbox first, then paste your real key when you are ready.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="sync-plan-note"]',
      title: "Your keys are safe",
      body: "Every credential is bank-grade encrypted the moment it lands on our servers. We only echo back the last 4 characters.",
      placement: "bottom",
    },
  ];
  const syncTour = useModuleTour("sync_v1", syncTourSteps);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-white/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the Sync Engine
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-8" data-testid="sync-engine-page">
      {syncTour.TourNode}
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-eyebrow gold flex items-center gap-2">
              <Waypoints className="w-3.5 h-3.5" /> Universal Data Sync Engine
            </div>
            <h1
              className="font-display text-3xl md:text-4xl mt-2 tracking-tight"
              style={{ textShadow: "0 0 24px rgba(212,175,55,0.25)" }}
            >
              Every supplier. One catalogue.
            </h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <SyncEnginePulse />
            <TourButton onClick={syncTour.start} testId="sync-tour-btn" />
          </div>
        </div>
        <p className="text-white/60 mt-3 max-w-3xl text-sm md:text-base">
          Wire PurchasePlus, MarketMan, and Fourth into the same pantry. Every invoice line maps to a local ingredient, purchase costs update the moment a supplier price moves, and the Digital Corporate Chef reads it all as live context. Global Reconcile runs every 24 hours in the background. You can also fire a Sync Now at any moment.
        </p>
        <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-100/90 flex items-start gap-2" data-testid="sync-plan-note">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Your API keys are bank-grade encrypted the moment they hit our servers. Only the last 4 characters of a saved key are ever shown back to you.
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
          <div className="font-medium text-white/90 mb-1">How this works, in plain language:</div>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Pick a provider below and hit Connect. That is it. No credentials needed on the first try.</li>
            <li>You will see a green toast: "Connected and synced". Sample invoices, cost updates, and any new ingredients land automatically. Scroll to the <strong>Invoices</strong> tab to see them.</li>
            <li>When you are ready for real data, click the provider again and paste your live API key from that provider's dashboard. Everything from step 2 now runs against your real supplier.</li>
          </ol>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sync-provider-grid">
        {providers.map((p) => {
          const conn = connByProvider[p.id];
          const status = conn?.status || "not_set_up";
          const isBusy = busy === p.id;
          return (
            <div
              key={p.id}
              className="rounded-2xl glass-card p-5 flex flex-col gap-4"
              data-testid={`sync-provider-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <ProviderMark provider={p} />
                  <div>
                    <div className="font-display text-xl leading-none">{p.name}</div>
                    <div className="text-[11px] text-white/50 mt-1">{p.tagline}</div>
                  </div>
                </div>
                <StatusChip status={status} />
              </div>

              {conn && (
                <div className="text-xs text-white/60 space-y-1">
                  {conn.label && (
                    <div className="flex justify-between gap-2">
                      <span className="text-white/40">Label</span>
                      <span className="text-white/80 truncate">{conn.label}</span>
                    </div>
                  )}
                  {conn.account_ref && (
                    <div className="flex justify-between gap-2">
                      <span className="text-white/40">{p.account_hint}</span>
                      <span className="tabular text-white/80 truncate">{conn.account_ref}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Auth</span>
                    <span className="tabular text-white/80">{conn.auth_mode === "oauth" ? "OAuth" : "API Key"}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Last sync</span>
                    <span className="tabular text-white/80">{formatTs(conn.last_sync_at)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Invoices synced</span>
                    <span className="tabular text-white/80">{conn.invoices_synced || 0}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Costs updated</span>
                    <span className="tabular text-white/80">{conn.ingredients_updated || 0}</span>
                  </div>
                  {conn.last_sync_message && (
                    <div className="text-[11px] text-white/50 mt-2 leading-relaxed">
                      {conn.last_sync_message}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-auto flex flex-wrap gap-2 pt-1">
                {conn ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleSync(p)}
                      disabled={isBusy}
                      data-testid={`sync-now-${p.id}`}
                    >
                      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      <span className="ml-1.5">Sync now</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConnect(p)}
                      data-testid={`sync-edit-${p.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(conn, p)}
                      data-testid={`sync-disconnect-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleConnect(p)}
                    data-testid={`sync-connect-${p.id}`}
                  >
                    <Waypoints className="w-3.5 h-3.5" />
                    <span className="ml-1.5">Connect</span>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="glass-card border border-white/10">
          <TabsTrigger value="invoices" data-testid="sync-tab-invoices">
            <Receipt className="w-3.5 h-3.5 mr-1.5" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="mappings" data-testid="sync-tab-mappings">
            <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" />
            SKU Mappings
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="sync-tab-history">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Cost updates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          {invoices.length === 0 ? (
            <EmptyState
              illustration="ledger"
              eyebrow="Invoices"
              title="No invoices synced yet"
              body="Connect a supplier above and hit Sync now. We will pull the last 30 days of invoices, tag each line item, and update your ingredient costs automatically."
            />
          ) : (
          <div className="rounded-2xl glass-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-white/[0.03] border-b border-white/10">
                <tr className="text-left text-[11px] uppercase tracking-wider text-white/50">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Line items</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody data-testid="sync-invoices-tbody">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 tabular text-white/80">{inv.invoice_date}</td>
                    <td className="px-4 py-3 text-white/70 capitalize">{(inv.provider || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 text-white/80">{inv.supplier_name}</td>
                    <td className="px-4 py-3 tabular text-white/60 text-xs">{inv.invoice_number}</td>
                    <td className="px-4 py-3 tabular text-white/70">{(inv.line_items || []).length}</td>
                    <td className="px-4 py-3 tabular text-right text-amber-100">{formatMoney(inv.total, inv.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </TabsContent>

        <TabsContent value="mappings" className="mt-4">
          {mappings.length === 0 ? (
            <EmptyState
              illustration="sync_gears"
              eyebrow="SKU Mappings"
              title="Mappings will grow as we sync"
              body="After your first supplier sync we match each SKU to your ingredient list. Any items we cannot auto match will surface here so you can approve them in one tap."
            />
          ) : (
          <div className="rounded-2xl glass-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-white/[0.03] border-b border-white/10">
                <tr className="text-left text-[11px] uppercase tracking-wider text-white/50">
                  <th className="px-4 py-3">Supplier SKU</th>
                  <th className="px-4 py-3">Supplier name</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Mapped ingredient id</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody data-testid="sync-mappings-tbody">
                {mappings.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 tabular text-white/80 text-xs">{m.external_sku}</td>
                    <td className="px-4 py-3 text-white/70">{m.external_name}</td>
                    <td className="px-4 py-3 text-white/70 capitalize">{(m.provider || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 tabular text-white/50 text-xs">{m.ingredient_id?.slice(0, 12)}...</td>
                    <td className="px-4 py-3 tabular text-white/50 text-xs">{formatTs(m.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {costHistory.length === 0 ? (
            <EmptyState
              illustration="ledger"
              eyebrow="Price History"
              title="No supplier price changes yet"
              body="Every time a supplier moves a price on an ingredient, we log the delta here. Your recipe margins refresh instantly so you always know the true cost of a plate."
            />
          ) : (
          <div className="rounded-2xl glass-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-white/[0.03] border-b border-white/10">
                <tr className="text-left text-[11px] uppercase tracking-wider text-white/50">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Ingredient</th>
                  <th className="px-4 py-3 text-right">Old</th>
                  <th className="px-4 py-3 text-right">New</th>
                  <th className="px-4 py-3 text-right">Delta</th>
                </tr>
              </thead>
              <tbody data-testid="sync-history-tbody">
                {costHistory.map((h) => {
                  const delta = Number(h.new_price || 0) - Number(h.old_price || 0);
                  const pct = h.old_price ? (delta / h.old_price) * 100 : 0;
                  const positive = delta >= 0;
                  return (
                    <tr key={h.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 tabular text-white/80">{(h.invoice_date || h.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-white/70 capitalize">{(h.provider || "").replace("_", " ")}</td>
                      <td className="px-4 py-3 tabular text-white/60 text-xs">{h.ingredient_id?.slice(0, 12)}...</td>
                      <td className="px-4 py-3 tabular text-right text-white/60">{Number(h.old_price).toFixed(2)}</td>
                      <td className="px-4 py-3 tabular text-right text-white/90">{Number(h.new_price).toFixed(2)}</td>
                      <td className={`px-4 py-3 tabular text-right ${positive ? "text-rose-300" : "text-emerald-300"}`}>
                        {positive ? "+" : ""}{delta.toFixed(2)} ({pct.toFixed(1)}%)
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </TabsContent>
      </Tabs>

      <ConnectionDialog
        open={!!dialog}
        dialog={dialog}
        onClose={() => setDialog(null)}
        onSave={submitConnect}
      />
    </div>
  );
}
